import { ROUTES } from '../../constants';

/** Cause d'un échec de connexion SSO, jointe en query par la boutique. */
export const SSO_ERROR_PARAM = 'sso_error';

/** sessionStorage et non le store redux : un rechargement complet s'interpose. */
const STORAGE_KEY = 'sso_error';

/** Au-delà, la cause n'a trouvé aucune page pour s'afficher et est jetée. */
const PENDING_TTL_MS = 60000;

/** Miroir des constantes `SsoController::FLASH_*` de la boutique. */
const SSO_ERROR_MESSAGE_KEYS: Record<string, string> = {
  session_invalid: 'auth.ssoError.session_invalid',
  identity_incomplete: 'auth.ssoError.identity_incomplete',
  account_unavailable: 'auth.ssoError.account_unavailable',
  channel_misconfigured: 'auth.ssoError.channel_misconfigured',
  login_required: 'auth.ssoError.login_required',
};

export const SSO_ERROR_FALLBACK_KEY = 'auth.ssoError.unknown';

/** `hasOwnProperty` car le code vient de l'URL : `constructor` serait truthy. */
export const ssoErrorMessageKey = (code: string): string =>
  Object.prototype.hasOwnProperty.call(SSO_ERROR_MESSAGE_KEYS, code)
    ? SSO_ERROR_MESSAGE_KEYS[code]
    : SSO_ERROR_FALLBACK_KEY;

/**
 * Déplace la cause de l'URL vers `sessionStorage`, à appeler avant le premier
 * render : `PrivateRoute` recopierait sinon `sso_error` dans son `returnPath`.
 */
export const stashSsoErrorFromUrl = (): void => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get(SSO_ERROR_PARAM);

  if (code === null) {
    return;
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, `${Date.now()}:${code}`);
  } catch {
    // Stockage refusé : l'URL doit être nettoyée quand même.
  }

  params.delete(SSO_ERROR_PARAM);
  const query = params.toString();

  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
  );
};

export const clearPendingSsoError = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Si l'écriture a échoué, la lecture aussi.
  }
};

/** Cause en attente, ou `null` si absente, illisible ou trop ancienne. */
export const pendingSsoErrorCode = (): string | null => {
  let raw: string | null = null;

  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }

  if (raw === null) {
    return null;
  }

  const separator = raw.indexOf(':');
  const stashedAt = separator === -1 ? NaN : Number(raw.slice(0, separator));
  const code = separator === -1 ? '' : raw.slice(separator + 1);

  if (
    code === '' ||
    !Number.isFinite(stashedAt) ||
    Date.now() - stashedAt > PENDING_TTL_MS
  ) {
    clearPendingSsoError();

    return null;
  }

  return code;
};

/**
 * Vrai quand un rechargement complet est imminent — visiteur encore
 * authentifié sur le login, que `useRedirectWhenAuthenticated` va renvoyer.
 */
export const shouldDeferSsoErrorFlash = (
  isAuthenticated: boolean,
  pathname: string,
): boolean =>
  isAuthenticated &&
  pathname.replace(/\/$/, '') === `${ROUTES.BASENAME}${ROUTES.LOGIN}`;
